import axios, { type InternalAxiosRequestConfig } from 'axios';
interface AuthRequestConfig extends InternalAxiosRequestConfig { sessionEpoch?: number }
let authEpoch = 0;
let sessionInvalidated = () => {};
export function setSessionInvalidatedHandler(handler: () => void) { sessionInvalidated = handler; }
async function authTransition<T>(work: () => Promise<T>): Promise<T> {
  authEpoch++;
  try { return await work(); } finally { authEpoch++; }
}

// Always make sure we're using relative URLs for API calls
const apiBaseUrl = import.meta.env.VITE_API_URL || '/api';
// For static assets, extract the base URL but still keep it relative
const baseUrl = apiBaseUrl.endsWith('/api') ? apiBaseUrl.replace('/api', '') : '';

export const api = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true, // send/receive HttpOnly auth cookie
  headers: {
    'Content-Type': 'application/json',
  }
});

// Read a cookie value by name (used for the CSRF double-submit token)
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  try { return match ? decodeURIComponent(match[1]) : null; } catch { return null; }
}

// Attach CSRF token header on state-changing requests (auth lives in HttpOnly cookie)
api.interceptors.request.use((config) => {
  (config as AuthRequestConfig).sessionEpoch = authEpoch;
  const method = (config.method || 'get').toLowerCase();
  if (!['get', 'head', 'options'].includes(method)) {
    const csrf = getCookie('csrf_token');
    if (csrf) {
      config.headers = config.headers || {};
      config.headers.set('X-CSRF-Token', csrf);
    }
  }
  return config;
});

// Ignore stale401s from requests started before a newer auth transition.
api.interceptors.response.use(response => response, (error: unknown) => {
  if (axios.isAxiosError(error) && error.response?.status === 401 &&
      error.response.data?.code === 'SESSION_INVALID' &&
      (error.config as AuthRequestConfig | undefined)?.sessionEpoch === authEpoch) {
    authEpoch++; sessionInvalidated();
  }
  return Promise.reject(error);
});

// Secure media URL helpers
export const mediaHelpers = {
  /**
   * Get a secure, temporary URL for media access
   * @param filePath - Relative path to the media file
   * @returns Promise with the secure URL
   */
  getSecureUrl: async (filePath: string): Promise<string> => {
    try {
      // Remove any leading slashes for consistent path formatting
      const normalizedPath = filePath.replace(/^\/+/, '');
      // Get a secure token from the API
      const response = await api.get(`/media-token/${normalizedPath}`);
      const { token } = response.data;
      // Form the complete URL with the token
      return `${baseUrl}/media-access/${token}`;
    } catch (error) {
      console.error('Error getting secure media URL:', error);
      throw error;
    }
  },

  /**
   * Convert a regular media URL to a secure URL with token
   * @param url - Original media URL 
   * @returns Promise with the secure URL
   */
  secureMediaUrl: async (url: string): Promise<string> => {
    try {
      // Skip if not a media URL
      if (!url || !url.includes('/music/')) {
        return url;
      }

      // Extract the file path from the URL
      const filePath = url.split('/music/')[1];
      if (!filePath) {
        console.error('Invalid media URL format:', url);
        return url;
      }

      // Get secure URL with token
      return await mediaHelpers.getSecureUrl(filePath);
    } catch (error) {
      console.error('Error converting to secure URL:', error, 'Original URL:', url);
      return url; // Fall back to original URL
    }
  }
};

// Auth errors must reject. Never log Axios errors: config.data can contain passwords.
export const auth = {
  login: (email: string, password: string, rememberMe = false) => authTransition(async () => {
    const response = await api.post('/auth/login', { email, password, rememberMe });
    if (response.data?.success !== true) throw new Error('Invalid login response');
    return response.data;
  }),
  logout: () => authTransition(async () => {
    const response = await api.post('/auth/logout');
    if (response.data?.success !== true) throw new Error('Invalid logout response');
    // Legacy JWT storage is removed only as cleanup, never used for auth.
    try { localStorage.removeItem('auth_token'); sessionStorage.removeItem('auth_token'); } catch { /* Storage may be disabled. */ }
  }),
  me: async () => {
    const response = await api.get('/auth/me');
    if (response.data?.authenticated !== true) throw new Error('Invalid authentication response');
    return response.data;
  },
  changePassword: (currentPassword: string, newPassword: string) => authTransition(async () => {
    const response = await api.post('/auth/change-password', { currentPassword, newPassword });
    return response.data;
  }),
  changeEmail: (currentPassword: string, newEmail: string) => authTransition(async () => {
    const response = await api.post('/auth/change-email', { currentPassword, newEmail });
    return response.data;
  }),
};

// Albums API
export const albums = {
  getAll: async () => {
    const response = await api.get('/albums');
    return response.data;
  },

  get: async (id: string) => {
    const response = await api.get(`/albums/${id}`);
    return response.data;
  },

  create: async (formData: FormData) => {
    const response = await api.post('/albums', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  update: async (id: string, formData: FormData) => {
    const response = await api.put(`/albums/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/albums/${id}`);
    return response.data;
  }
};

// Songs API
export const songs = {
  getAll: async () => {
    const response = await api.get('/songs');
    return response.data;
  },

  get: async (id: string) => {
    const response = await api.get(`/songs/${id}`);
    return response.data;
  },

  create: async (formData: FormData) => {
    const response = await api.post('/songs', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  update: async (id: string, formData: FormData) => {
    const response = await api.put(`/songs/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/songs/${id}`);
    return response.data;
  },

  updateLyrics: async (id: string, lyrics: any) => {
    const response = await api.put(`/songs/${id}/lyrics`, { lyrics });
    return response.data;
  }
};

// Site API
export const site = {
  getContent: async () => {
    const response = await api.get('/site/content');
    return response.data;
  },
  
  updateContent: async (content: { title: string, subtitle: string, social_links: Array<{ icon: string, url: string, label: string }> }) => {
    const response = await api.put('/site/content', content);
    return response.data;
  }
};

export default {
  auth,
  albums,
  songs,
  site
}; 